import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * TransactionTable - Shared transaction drill-down component
 *
 * MUI-free, CSS-variable themed. Both apps consume this:
 *   - GAS: uses callServer for data fetching
 *   - TekStak: uses react-query wrapper
 *
 * Render slots allow app-specific features without coupling:
 *   - renderClassification: TekStak injects ClassificationChip
 *   - renderPoLink: TekStak injects PoLink
 *   - renderFooterActions: TekStak injects AgentTrigger/flag
 */
import React, { useState, useEffect, useMemo, useCallback, useRef, } from 'react';
import { ChevronRight, ChevronDown, ChevronUp, Layers, List, Loader2, Copy, Check, AlertCircle, AlertTriangle, ArrowUpDown, Filter, Search, FileJson, FileText, Table as TableIcon, ChevronLeft, } from 'lucide-react';
import { toJSON, toMarkdown, toPlainText } from './copyFormats.js';
import { formatDate } from '@techstack/core';
// =============================================================================
// TIME PERIODS
// =============================================================================
const TIME_PERIODS = [
    { key: '1w', label: '1W', days: 7 },
    { key: '1m', label: '1M', days: 30 },
    { key: '3m', label: '3M', days: 90 },
    { key: '6m', label: '6M', days: 180 },
    { key: '1y', label: '1Y', days: 365 },
    { key: '2y', label: '2Y', days: 730 },
    { key: '3y', label: '3Y', days: 1095 },
    { key: '5y', label: '5Y', days: 1825 },
    { key: 'all', label: 'All', days: null },
];
function getDateCutoff(periodKey) {
    const period = TIME_PERIODS.find(p => p.key === periodKey);
    if (!period || period.days === null)
        return null;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - period.days);
    cutoff.setHours(0, 0, 0, 0);
    return cutoff;
}
/**
 * Compute start/end date strings from a time period key.
 * Exported so parent components can use the same logic.
 */
export function getDateRangeFromTimePeriod(periodKey) {
    if (periodKey === 'all') {
        return { startDate: null, endDate: null };
    }
    const cutoff = getDateCutoff(periodKey);
    if (!cutoff) {
        return { startDate: null, endDate: null };
    }
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    return {
        startDate: formatDate(cutoff),
        endDate: formatDate(today),
    };
}
function rowMatchesTimePeriod(row, periodKey) {
    if (periodKey === 'all')
        return true;
    const cutoff = getDateCutoff(periodKey);
    if (!cutoff)
        return true;
    const dateStr = row.start_date;
    if (!dateStr)
        return false; // no date = exclude from time filter
    const rowDate = new Date(dateStr);
    if (isNaN(rowDate.getTime()))
        return false;
    return rowDate >= cutoff;
}
// =============================================================================
// FORMATTERS
// =============================================================================
export function fmtCurrency(n) {
    const v = Number(n) || 0;
    if (v >= 1e9)
        return `$${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6)
        return `$${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3)
        return `$${(v / 1e3).toFixed(0)}K`;
    return `$${Math.round(v).toLocaleString()}`;
}
/** Exact currency for reconciliation display — never compressed to K/M. */
export function fmtCurrencyExact(n) {
    const v = Number(n) || 0;
    const sign = v < 0 ? '-' : '';
    const hasCents = Math.round(Math.abs(v) * 100) % 100 !== 0;
    return `${sign}$${Math.abs(v).toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
    })}`;
}
/** Compare two dollar amounts in integer cents; equal within 1 cent. */
export function centsEqual(a, b) {
    return Math.abs(Math.round(a * 100) - Math.round(b * 100)) <= 1;
}
/*
 * "Jun 30, 26" was neither convention, and Ryan called it: a written-out date
 * takes a written-out year — "Jun 30, 2026" — and a two-digit year belongs to
 * the all-numeric form, "6/30/26". A short month beside a clipped year is the
 * mix of the two, and it reads as a typo.
 *
 * Written-out is the one to keep here, because this modal already prints
 * "Jul 1, 2025" through fmtDateFull for its window chip; the table was the
 * only thing in the dialog speaking a different dialect.
 */
function fmtDateShort(d) {
    if (!d)
        return '—';
    return new Date(d).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}
/** Day and month only — for the near end of a range that shares its year. */
function fmtDayMonth(d) {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
/** Full date for the window scope chip (e.g., "Jul 1, 2025"); null passthrough.
 *  X-16: shared formatter (core) — its 'medium' style parses date-only strings
 *  as LOCAL midnight, so a 'YYYY-MM-DD' bound doesn't render a day early west
 *  of Greenwich. */
function fmtDateFull(d) {
    return formatDate(d, 'medium', 'en-US') || null;
}
/** Format date as MMM YYYY (e.g., "Jan 2024") - concise for column display */
function fmtDateMonthYear(d) {
    if (!d)
        return '—';
    const date = new Date(d);
    if (isNaN(date.getTime()))
        return '—';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
    });
}
// =============================================================================
// CONFIDENCE HELPERS
// =============================================================================
function confidenceBand(conf) {
    if (conf == null)
        return 'low';
    const pct = conf * 100;
    if (pct >= 85)
        return 'high';
    if (pct >= 60)
        return 'mid';
    return 'low';
}
function isLowerConfidenceRow(row, mode) {
    // In 'confirmed' mode, all rows pass the strict filter, so none are "lower"
    // In 'probable' or 'all' mode, rows with confidence < 0.85 and no vendor evidence
    // are considered "lower confidence" for visual flagging
    if (mode === 'confirmed')
        return false;
    const conf = row.component_confidence ?? 0;
    return conf < 0.85;
}
// =============================================================================
// DEFAULT COLUMNS
// =============================================================================
// Responsive priority contract:
//   priority 0/undefined — always visible: PO #, Agency, Partner, Date, Value.
//   priority >0 — droppable when the container narrows; HIGHER drops FIRST:
//   Method (5) → Line (4) → Conf (3) → Classification (2) → Description (1).
// Dropped columns' data moves into the expandable row detail.
const DEFAULT_COLUMNS = [
    {
        key: 'purchase_document_num',
        label: 'PO #',
        sortType: 'string',
        filterMode: 'multiselect',
        width: '100px',
        minPx: 100,
    },
    {
        key: 'purchase_document_line',
        label: 'Line',
        sortType: 'number',
        hideInGrouped: true,
        width: '50px',
        priority: 4,
        minPx: 48,
    },
    {
        key: 'agency',
        label: 'Agency',
        sortType: 'string',
        filterMode: 'multiselect',
        width: '100px',
        minPx: 90,
    },
    {
        key: 'item_description',
        label: 'Description',
        sortType: 'string',
        filterMode: 'text',
        truncate: 140,
        priority: 1,
        minPx: 200,
    },
    {
        key: 'component_name',
        label: 'Classification',
        sortType: 'string',
        filterMode: 'multiselect',
        width: '140px',
        priority: 2,
        minPx: 140,
    },
    {
        key: 'supplier_name',
        label: 'Partner',
        sortType: 'string',
        filterMode: 'multiselect',
        width: '120px',
        minPx: 110,
    },
    {
        key: 'start_date',
        label: 'Date',
        sortType: 'date',
        width: '90px',
        minPx: 84,
    },
    {
        key: 'component_confidence',
        label: 'Conf',
        align: 'right',
        sortType: 'number',
        filterMode: 'number',
        width: '70px',
        priority: 3,
        minPx: 64,
    },
    {
        key: 'classification_method',
        label: 'Method',
        sortType: 'string',
        filterMode: 'multiselect',
        width: '90px',
        priority: 5,
        minPx: 90,
    },
    {
        key: 'line_total',
        label: 'Value',
        align: 'right',
        sortType: 'number',
        filterMode: 'number',
        width: '100px',
        minPx: 96,
    },
];
// =============================================================================
// SORT + FILTER HELPERS
// =============================================================================
function getCellValue(row, col) {
    return row[col.key];
}
function compareValues(a, b, sortType = 'string') {
    if (a == null && b == null)
        return 0;
    if (a == null)
        return 1;
    if (b == null)
        return -1;
    if (sortType === 'number') {
        return (Number(a) || 0) - (Number(b) || 0);
    }
    if (sortType === 'date') {
        return new Date(a).getTime() - new Date(b).getTime();
    }
    return String(a).localeCompare(String(b));
}
function rowMatchesFilter(row, col, filter) {
    if (!filter)
        return true;
    const v = getCellValue(row, col);
    if (col.filterMode === 'multiselect' && 'values' in filter) {
        if (filter.values.size === 0)
            return true;
        const key = v == null || v === '' ? '__empty__' : String(v);
        return filter.values.has(key);
    }
    if (col.filterMode === 'text' && 'text' in filter) {
        const q = (filter.text || '').trim().toLowerCase();
        if (!q)
            return true;
        return String(v || '').toLowerCase().includes(q);
    }
    if (col.filterMode === 'number' && ('min' in filter || 'max' in filter)) {
        const n = Number(v);
        if (filter.min != null && (!Number.isFinite(n) || n < filter.min))
            return false;
        if (filter.max != null && (!Number.isFinite(n) || n > filter.max))
            return false;
    }
    return true;
}
function isFilterActive(filter, filterMode) {
    if (!filter)
        return false;
    if (filterMode === 'multiselect' && 'values' in filter) {
        return filter.values.size > 0;
    }
    if (filterMode === 'text' && 'text' in filter) {
        return !!(filter.text && filter.text.trim());
    }
    if (filterMode === 'number') {
        return ('min' in filter && filter.min != null) || ('max' in filter && filter.max != null);
    }
    return false;
}
function FilterMenu({ col, rows, filter, onApply, onClose }) {
    const ref = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                onClose();
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);
    const [draft, setDraft] = useState(() => ({
        values: new Set((filter && 'values' in filter) ? filter.values : []),
        text: (filter && 'text' in filter) ? filter.text : '',
        min: (filter && 'min' in filter) ? String(filter.min ?? '') : '',
        max: (filter && 'max' in filter) ? String(filter.max ?? '') : '',
    }));
    const uniqueValues = useMemo(() => {
        if (col.filterMode !== 'multiselect')
            return [];
        const counts = new Map();
        for (const r of rows) {
            const v = getCellValue(r, col);
            const key = v == null || v === '' ? '__empty__' : String(v);
            counts.set(key, (counts.get(key) || 0) + 1);
        }
        return [...counts.entries()]
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([key, count]) => ({
            key,
            count,
            display: key === '__empty__' ? '(empty)' : key,
        }));
    }, [rows, col]);
    const [search, setSearch] = useState('');
    const filteredOptions = useMemo(() => {
        if (!search)
            return uniqueValues;
        const q = search.toLowerCase();
        return uniqueValues.filter((o) => o.display.toLowerCase().includes(q));
    }, [uniqueValues, search]);
    const toggleValue = (key) => {
        setDraft((d) => {
            const next = new Set(d.values);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return { ...d, values: next };
        });
    };
    const apply = () => {
        if (col.filterMode === 'multiselect') {
            onApply(draft.values.size > 0 ? { values: draft.values } : null);
        }
        else if (col.filterMode === 'text') {
            onApply(draft.text.trim() ? { text: draft.text.trim() } : null);
        }
        else if (col.filterMode === 'number') {
            const min = draft.min !== '' && Number.isFinite(Number(draft.min))
                ? Number(draft.min)
                : null;
            const max = draft.max !== '' && Number.isFinite(Number(draft.max))
                ? Number(draft.max)
                : null;
            onApply(min != null || max != null ? { min, max } : null);
        }
        onClose();
    };
    const clear = () => {
        onApply(null);
        onClose();
    };
    return (_jsxs("div", { className: "tt-filter-menu", ref: ref, role: "menu", children: [_jsx("div", { className: "tt-filter-menu-head", children: _jsxs("strong", { children: ["Filter \u00B7 ", col.label] }) }), col.filterMode === 'multiselect' && (_jsxs(_Fragment, { children: [uniqueValues.length > 8 && (_jsxs("div", { className: "tt-filter-menu-search", children: [_jsx(Search, { size: 12 }), _jsx("input", { value: search, onChange: (e) => setSearch(e.target.value), placeholder: "search values\u2026", autoFocus: true })] })), _jsxs("div", { className: "tt-filter-menu-list", children: [filteredOptions.slice(0, 100).map((opt) => (_jsxs("label", { className: "tt-filter-menu-item", children: [_jsx("input", { type: "checkbox", checked: draft.values.has(opt.key), onChange: () => toggleValue(opt.key) }), _jsx("span", { className: "tt-filter-menu-label", children: opt.display }), _jsx("span", { className: "tt-filter-menu-count", children: opt.count })] }, opt.key))), filteredOptions.length === 0 && (_jsx("div", { style: { padding: '0.5rem', textAlign: 'center', color: 'var(--gray-500)' }, children: "No matches." }))] })] })), col.filterMode === 'text' && (_jsx("div", { className: "tt-filter-menu-text", children: _jsx("input", { value: draft.text, onChange: (e) => setDraft((d) => ({ ...d, text: e.target.value })), placeholder: "contains\u2026", autoFocus: true }) })), col.filterMode === 'number' && (_jsxs("div", { className: "tt-filter-menu-range", children: [_jsxs("label", { children: [_jsx("span", { children: "Min" }), _jsx("input", { type: "number", value: draft.min, onChange: (e) => setDraft((d) => ({ ...d, min: e.target.value })), placeholder: "e.g. 0" })] }), _jsxs("label", { children: [_jsx("span", { children: "Max" }), _jsx("input", { type: "number", value: draft.max, onChange: (e) => setDraft((d) => ({ ...d, max: e.target.value })), placeholder: "e.g. 1" })] }), col.key === 'component_confidence' && (_jsx("p", { style: { margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)' }, children: "Confidence is 0\u20131 (0.85 = 85%)." }))] })), _jsxs("div", { className: "tt-filter-menu-actions", children: [_jsx("button", { className: "tt-btn tt-btn-ghost", onClick: clear, children: "Clear" }), _jsx("button", { className: "tt-btn", onClick: apply, children: "Apply" })] })] }));
}
function ColumnHeader({ col, sort, filter, rows, onSortChange, onFilterChange, }) {
    const [filterOpen, setFilterOpen] = useState(false);
    const sortActive = sort?.col === col.key;
    const filterActive = isFilterActive(filter, col.filterMode ?? null);
    const cycleSort = () => {
        if (!sortActive)
            onSortChange({ col: col.key, dir: 'asc' });
        else if (sort?.dir === 'asc')
            onSortChange({ col: col.key, dir: 'desc' });
        else
            onSortChange(null);
    };
    const alignClass = col.align === 'right'
        ? 'tt-th--right'
        : col.align === 'center'
            ? 'tt-th--center'
            : '';
    return (_jsxs("th", { className: `tt-th ${alignClass}`, style: { width: col.width }, children: [_jsxs("div", { className: "tt-th-inner", children: [_jsxs("button", { type: "button", className: `tt-th-sort ${sortActive ? 'is-active' : ''}`, onClick: cycleSort, title: "Click to sort", children: [_jsx("span", { children: col.label }), sortActive ? (sort?.dir === 'asc' ? (_jsx(ChevronUp, { size: 12 })) : (_jsx(ChevronDown, { size: 12 }))) : (_jsx(ArrowUpDown, { size: 11, className: "tt-th-sort-icon" }))] }), col.filterMode && (_jsxs("button", { type: "button", className: `tt-th-filter ${filterActive ? 'is-active' : ''}`, onClick: () => setFilterOpen((o) => !o), title: filterActive ? 'Filter active — click to edit' : 'Filter', "aria-haspopup": "menu", "aria-expanded": filterOpen, children: [_jsx(Filter, { size: 11 }), filterActive && _jsx("span", { className: "tt-th-filter-dot", "aria-hidden": "true" })] }))] }), filterOpen && col.filterMode && (_jsx(FilterMenu, { col: col, rows: rows, filter: filter, onApply: (next) => onFilterChange(col.key, next), onClose: () => setFilterOpen(false) }))] }));
}
function TimePeriodFilter({ selected, onChange, totalCount, filteredCount }) {
    const showCount = selected !== 'all' && filteredCount !== totalCount;
    return (_jsxs("div", { className: "tt-time-filter", children: [_jsx("div", { className: "tt-time-pills", children: TIME_PERIODS.map(p => (_jsx("button", { type: "button", className: `tt-time-pill ${selected === p.key ? 'is-active' : ''}`, onClick: () => onChange(p.key), children: p.label }, p.key))) }), showCount && (_jsxs("span", { className: "tt-time-count", children: [filteredCount.toLocaleString(), " of ", totalCount.toLocaleString(), " in window"] }))] }));
}
function CopyMenu({ rows, context }) {
    const [open, setOpen] = useState(false);
    const [recent, setRecent] = useState(null);
    const ref = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);
    const copy = useCallback(async (format, formatter) => {
        try {
            const text = formatter(rows, context);
            await navigator.clipboard.writeText(text);
            setRecent(format);
            setTimeout(() => setRecent(null), 1500);
            setOpen(false);
        }
        catch (err) {
            console.warn('Copy failed:', err);
        }
    }, [rows, context]);
    const disabled = !rows || rows.length === 0;
    return (_jsxs("div", { className: "tt-copy-menu", ref: ref, children: [_jsxs("button", { className: "tt-btn", disabled: disabled, onClick: () => setOpen((o) => !o), title: "Copy rows for LLM chat / clipboard", children: [recent ? _jsx(Check, { size: 14 }) : _jsx(Copy, { size: 14 }), _jsx("span", { children: recent ? 'Copied' : 'Copy' })] }), open && !disabled && (_jsxs("div", { className: "tt-copy-dropdown", role: "menu", children: [_jsxs("button", { className: "tt-copy-item", onClick: () => copy('md', toMarkdown), title: "Markdown table \u2014 best for pasting into Claude / ChatGPT", children: [_jsx(TableIcon, { size: 14 }), " Markdown table"] }), _jsxs("button", { className: "tt-copy-item", onClick: () => copy('json', toJSON), title: "JSON \u2014 structured shape for programmatic use", children: [_jsx(FileJson, { size: 14 }), " JSON"] }), _jsxs("button", { className: "tt-copy-item", onClick: () => copy('text', toPlainText), title: "Plain text \u2014 compact summary for inline chat", children: [_jsx(FileText, { size: 14 }), " Plain text"] })] }))] }));
}
// =============================================================================
// AGGREGATE CELL CONTENT (for grouped PO headers)
// =============================================================================
function aggregateCellContent(col, lines) {
    const values = lines
        .map((r) => getCellValue(r, col))
        .filter((v) => v != null && v !== '');
    if (values.length === 0)
        return _jsx("span", { className: "tt-td--muted", children: "\u2014" });
    if (col.sortType === 'number') {
        const nums = values.map(Number).filter(Number.isFinite);
        if (!nums.length)
            return _jsx("span", { className: "tt-td--muted", children: "\u2014" });
        const sum = nums.reduce((a, b) => a + b, 0);
        if (col.key === 'line_total') {
            return fmtCurrency(sum);
        }
        const avg = sum / nums.length;
        return (_jsxs("span", { title: `average of ${nums.length} lines`, children: [col.key === 'component_confidence' ? `${Math.round(avg * 100)}%` : avg.toFixed(1), nums.length > 1 && _jsx("span", { className: "tt-td--muted", children: " avg" })] }));
    }
    if (col.sortType === 'date') {
        const dated = values
            .map((v) => ({ raw: v, d: new Date(v) }))
            .filter((x) => !isNaN(x.d.getTime()));
        if (!dated.length)
            return _jsx("span", { className: "tt-td--muted", children: "\u2014" });
        dated.sort((a, b) => a.d.getTime() - b.d.getTime());
        const min = dated[0];
        const max = dated[dated.length - 1];
        if (min.d.getTime() === max.d.getTime()) {
            return fmtDateShort(min.raw);
        }
        /*
         * A four-digit year costs two characters twice over in a range, and a
         * range almost always sits inside ONE fiscal year — so the year is stated
         * once, at the end: "Apr 1 – Jun 30, 2026". That is both shorter than the
         * two-digit version it replaces and unambiguous, which is the rare case
         * where the more correct format is also the more compact one.
         */
        const sameYear = min.d.getFullYear() === max.d.getFullYear();
        return (_jsxs("span", { title: `${dated.length} dates`, children: [sameYear ? fmtDayMonth(min.raw) : fmtDateShort(min.raw), ' – ', fmtDateShort(max.raw)] }));
    }
    // String aggregation
    const unique = [...new Set(values.map(String))];
    if (unique.length === 1) {
        return unique[0];
    }
    return (_jsxs("span", { className: "tt-td--muted", title: unique.slice(0, 8).join(', '), children: ["Various (", unique.length, ")"] }));
}
function PoGroupRow({ poGroup, columns, hiddenColumns = [], expandedDetails, onToggleDetail, isExpanded, onToggle, confidenceMode, renderClassification, renderPoLink, renderFooterActions, onRowClick, }) {
    const { poNum, totalAmount, lineCount, lines } = poGroup;
    const sample = lines[0];
    const groupedColumns = columns.filter((c) => !c.hideInGrouped);
    const hasActions = !!renderFooterActions;
    const hasHidden = hiddenColumns.length > 0;
    return (_jsxs(_Fragment, { children: [_jsxs("tr", { className: "tt-group-row", onClick: onToggle, children: [hasHidden && _jsx("td", { className: "tt-td tt-expander-col" }), groupedColumns.map((col, idx) => {
                        // First column: PO toggle
                        if (idx === 0) {
                            return (_jsxs("td", { className: "tt-td tt-td--mono tt-group-toggle", children: [isExpanded ? _jsx(ChevronDown, { size: 14 }) : _jsx(ChevronRight, { size: 14 }), renderPoLink ? (renderPoLink(sample)) : (_jsx("span", { children: poNum === 'NO_PO' ? '(no PO)' : poNum }))] }, col.key));
                        }
                        // Description column: line count
                        if (col.key === 'item_description') {
                            return (_jsx("td", { className: "tt-td", children: _jsxs("span", { className: "tt-line-count", children: [lineCount, " line", lineCount !== 1 ? 's' : ''] }) }, col.key));
                        }
                        // Amount column: PO total
                        if (col.key === 'line_total') {
                            return (_jsx("td", { className: "tt-td tt-td--right tt-group-total", children: fmtCurrency(totalAmount) }, col.key));
                        }
                        // Classification column: use renderClassification or aggregate
                        if (col.key === 'component_name' && renderClassification) {
                            // For grouped view, render first line's classification
                            return (_jsx("td", { className: "tt-td", children: aggregateCellContent(col, lines) }, col.key));
                        }
                        // Everything else: aggregate
                        const alignClass = col.align === 'right'
                            ? 'tt-td--right'
                            : col.align === 'center'
                                ? 'tt-td--center'
                                : '';
                        return (_jsx("td", { className: `tt-td ${alignClass}`, children: aggregateCellContent(col, lines) }, col.key));
                    }), hasActions && _jsx("td", { className: "tt-td tt-actions-cell" })] }), isExpanded &&
                lines.map((r) => {
                    const isLower = isLowerConfidenceRow(r, confidenceMode);
                    const isDetailOpen = expandedDetails?.has(r.id) ?? false;
                    return (_jsxs(React.Fragment, { children: [_jsxs("tr", { className: `tt-nested-row ${isLower ? 'tt-row-lower-confidence' : ''}`, onClick: (e) => {
                                    e.stopPropagation();
                                    onRowClick?.(r);
                                }, style: { cursor: onRowClick ? 'pointer' : 'default' }, children: [hasHidden && (_jsx("td", { className: "tt-td tt-expander-col", children: onToggleDetail && (_jsx(DetailToggle, { expanded: isDetailOpen, onToggle: () => onToggleDetail(r.id) })) })), columns.map((col, idx) => {
                                        if (idx === 0) {
                                            return (_jsxs("td", { className: "tt-td tt-nested-line", children: [_jsx("span", { className: "tt-line-indicator", children: "\u2514" }), "Line ", r.purchase_document_line || '?'] }, col.key));
                                        }
                                        if (col.key === 'purchase_document_line')
                                            return null;
                                        return (_jsx(RenderCell, { col: col, row: r, confidenceMode: confidenceMode, renderClassification: renderClassification }, col.key));
                                    }), hasActions && (_jsx("td", { className: "tt-td tt-actions-cell", children: renderFooterActions(r) }))] }), hasHidden && isDetailOpen && (_jsx(HiddenDetailRow, { row: r, hiddenColumns: hiddenColumns, colSpan: groupedColumns.length + 1 + (hasActions ? 1 : 0) }))] }, r.id));
                })] }));
}
function RenderCell({ col, row, confidenceMode, renderClassification }) {
    const raw = getCellValue(row, col);
    const isLower = isLowerConfidenceRow(row, confidenceMode);
    // Custom column renderer
    if (col.render) {
        return (_jsx("td", { className: `tt-td ${col.align === 'right' ? 'tt-td--right' : ''}`, children: col.render(raw, row) }));
    }
    // Classification column with render slot
    if (col.key === 'component_name') {
        if (renderClassification) {
            return _jsx("td", { className: "tt-td", children: renderClassification(row) });
        }
        // Default: plain text + confidence badge
        const conf = row.component_confidence;
        const band = confidenceBand(conf);
        return (_jsx("td", { className: "tt-td", children: _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }, children: [_jsx("span", { children: String(raw || '—') }), conf != null && (_jsxs("span", { className: `tt-confidence tt-confidence--${band}`, children: [Math.round(conf * 100), "%"] })), isLower && confidenceMode !== 'confirmed' && (_jsxs("span", { className: "tt-unverified-badge", children: [_jsx(AlertTriangle, { size: 10 }), " verify"] }))] }) }));
    }
    // Confidence column
    if (col.key === 'component_confidence') {
        const conf = raw;
        if (conf == null)
            return _jsx("td", { className: "tt-td tt-td--right tt-td--muted", children: "\u2014" });
        const band = confidenceBand(conf);
        return (_jsx("td", { className: "tt-td tt-td--right", children: _jsxs("span", { className: `tt-confidence tt-confidence--${band}`, children: [Math.round(conf * 100), "%"] }) }));
    }
    // Date column
    if (col.key === 'start_date') {
        return _jsx("td", { className: "tt-td", children: fmtDateMonthYear(raw) });
    }
    // Value column — title carries the exact amount (display is compressed)
    if (col.key === 'line_total') {
        return (_jsx("td", { className: "tt-td tt-td--right tt-td--value", title: fmtCurrencyExact(raw), children: fmtCurrency(raw) }));
    }
    // Method column
    if (col.key === 'classification_method') {
        return _jsx("td", { className: "tt-td tt-td--mono tt-td--muted", children: String(raw || '—') });
    }
    // Description with truncate
    if (col.truncate && typeof raw === 'string' && raw.length > col.truncate) {
        return (_jsxs("td", { className: "tt-td tt-td--truncate", title: raw, children: [raw.slice(0, col.truncate), "\u2026"] }));
    }
    // Default
    const alignClass = col.align === 'right'
        ? 'tt-td--right'
        : col.align === 'center'
            ? 'tt-td--center'
            : '';
    return _jsx("td", { className: `tt-td ${alignClass}`, children: raw != null ? String(raw) : '—' });
}
// =============================================================================
// PHONE CARD VIEW (containers narrower than the core columns' minimums)
// The always-visible columns alone need ~480px; below that, table-layout:
// fixed crushed every cell to an ellipsis. On phones the drilldown renders
// as stacked cards instead — PO/description + value, agency - date,
// partner, classification + actions. Sorting/filtering/grouping and the
// toolbar all keep working; only the presentation changes.
// =============================================================================
function PhoneLineCard({ row, confidenceMode, showPo, renderClassification, renderFooterActions, }) {
    return (_jsxs("div", { className: `tt-card tt-card--line ${isLowerConfidenceRow(row, confidenceMode) ? 'tt-row-lower-confidence' : ''}`, children: [_jsxs("div", { className: "tt-card-head", children: [_jsx("span", { className: "tt-card-desc", children: row.item_description || '(no description)' }), _jsx("span", { className: "tt-card-value", children: fmtCurrency(Number(row.line_total) || 0) })] }), _jsxs("div", { className: "tt-card-meta", children: [showPo && row.purchase_document_num && (_jsx("span", { className: "tt-td--mono", children: row.purchase_document_num })), _jsx("span", { children: row.agency || '—' }), _jsx("span", { children: fmtDateFull(row.start_date) || '—' }), row.supplier_name && _jsx("span", { children: row.supplier_name })] }), (renderClassification || renderFooterActions) && (_jsxs("div", { className: "tt-card-foot", children: [_jsx("span", { className: "tt-card-chip", children: renderClassification ? renderClassification(row) : (row.component_name || null) }), renderFooterActions && _jsx("span", { className: "tt-card-actions", children: renderFooterActions(row) })] }))] }));
}
// =============================================================================
// HIDDEN-COLUMN DETAIL ROW (responsive priority overflow)
// =============================================================================
function detailValue(col, row) {
    const raw = getCellValue(row, col);
    if (raw == null || raw === '')
        return _jsx("span", { className: "tt-td--muted", children: "\u2014" });
    if (col.key === 'component_confidence') {
        const band = confidenceBand(raw);
        return (_jsxs("span", { className: `tt-confidence tt-confidence--${band}`, children: [Math.round(raw * 100), "%"] }));
    }
    if (col.key === 'line_total')
        return fmtCurrencyExact(raw);
    if (col.sortType === 'date')
        return fmtDateShort(raw);
    if (col.key === 'classification_method') {
        return _jsx("span", { className: "tt-td--mono", children: String(raw) });
    }
    return String(raw);
}
function HiddenDetailRow({ row, hiddenColumns, colSpan }) {
    return (_jsx("tr", { className: "tt-detail-row", children: _jsx("td", { className: "tt-td tt-detail-cell", colSpan: colSpan, children: _jsx("div", { className: "tt-detail-grid", children: hiddenColumns.map((col) => (_jsxs("div", { className: "tt-detail-item", children: [_jsx("span", { className: "tt-detail-label", children: col.label }), _jsx("span", { className: "tt-detail-value", children: detailValue(col, row) })] }, col.key))) }) }) }));
}
function DetailToggle({ expanded, onToggle }) {
    return (_jsx("button", { type: "button", className: `tt-expander ${expanded ? 'is-open' : ''}`, onClick: (e) => {
            e.stopPropagation();
            onToggle();
        }, "aria-expanded": expanded, title: expanded ? 'Hide extra columns' : 'Show hidden columns', children: expanded ? _jsx(ChevronDown, { size: 13 }) : _jsx(ChevronRight, { size: 13 }) }));
}
// =============================================================================
// MAIN COMPONENT
// =============================================================================
export function TransactionTable({ 
// Scope
vendor, departmentId, component, productFamily, startDate, endDate, 
// Data
fetchTransactions, rows: rowsProp, totalRowCount: totalRowCountProp, totalSpend: totalSpendProp, isLoading: isLoadingProp, error: errorProp, 
// Confidence mode
defaultConfidenceMode = 'confirmed', hideConfidenceToggle = false, onConfidenceModeChange, 
// Presentation
groupByPo: groupByPoProp = true, columns = DEFAULT_COLUMNS, pageSize = 50, dense = false, emptyMessage = 'No transactions match these filters.', showTimePeriodFilter = false, defaultTimePeriod = 'all', onTimePeriodChange, timeScope, evidenceSplit, 
// Render slots
renderClassification, renderPoLink, renderFooterActions, 
// Callbacks
onRowClick, 
// Reconciliation
reconcile, 
// Copy
copyContext, 
// Styling
className = '', }) {
    // State
    const [confidenceMode, setConfidenceMode] = useState(defaultConfidenceMode);
    const [groupByPo, setGroupByPo] = useState(groupByPoProp);
    const [expandedPos, setExpandedPos] = useState(new Set());
    const [sort, setSort] = useState(null);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(0);
    const [timePeriod, setTimePeriod] = useState(defaultTimePeriod);
    // Responsive column priority: measure the CONTAINER (not the viewport —
    // the table usually lives in a modal) and drop droppable columns,
    // highest priority number first, when they don't fit.
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(null);
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries)
                setContainerWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    // Expanded per-row details showing hidden (deprioritized) columns' data
    const [expandedDetails, setExpandedDetails] = useState(new Set());
    const toggleDetail = useCallback((id) => {
        setExpandedDetails((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    // Internal fetch state (only used when fetchTransactions is provided)
    const [internalRows, setInternalRows] = useState([]);
    const [internalTotal, setInternalTotal] = useState(0);
    const [internalSpend, setInternalSpend] = useState(0);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalError, setInternalError] = useState(null);
    // Use direct data or internal fetch
    const isDirectMode = rowsProp !== undefined;
    const rows = isDirectMode ? rowsProp : internalRows;
    const totalRowCount = isDirectMode ? (totalRowCountProp ?? rows.length) : internalTotal;
    const totalSpend = isDirectMode ? (totalSpendProp ?? 0) : internalSpend;
    const isLoading = isDirectMode ? (isLoadingProp ?? false) : internalLoading;
    const error = isDirectMode ? (errorProp ?? null) : internalError;
    // Fetch data when params change (fetch mode only)
    useEffect(() => {
        if (isDirectMode || !fetchTransactions)
            return;
        const params = {
            vendor,
            startDate,
            endDate,
            departmentId,
            component,
            productFamily,
            confidenceMode,
            limit: pageSize,
            offset: page * pageSize,
        };
        setInternalLoading(true);
        setInternalError(null);
        fetchTransactions(params)
            .then((res) => {
            setInternalRows(res.rows);
            setInternalTotal(res.total_row_count);
            setInternalSpend(res.total_spend);
        })
            .catch((err) => {
            setInternalError(err);
            setInternalRows([]);
        })
            .finally(() => {
            setInternalLoading(false);
        });
    }, [
        isDirectMode,
        fetchTransactions,
        vendor,
        startDate,
        endDate,
        departmentId,
        component,
        productFamily,
        confidenceMode,
        pageSize,
        page,
    ]);
    // Handle confidence mode change
    const handleConfidenceModeChange = useCallback((mode) => {
        setConfidenceMode(mode);
        setPage(0);
        onConfidenceModeChange?.(mode);
    }, [onConfidenceModeChange]);
    // Handle time period change
    const handleTimePeriodChange = useCallback((newPeriod) => {
        setTimePeriod(newPeriod);
        setPage(0);
        if (onTimePeriodChange) {
            const { startDate: newStart, endDate: newEnd } = getDateRangeFromTimePeriod(newPeriod);
            onTimePeriodChange(newPeriod, newStart, newEnd);
        }
    }, [onTimePeriodChange]);
    // Column lookup - safely handle null/undefined columns
    const effectiveColumns = columns || DEFAULT_COLUMNS;
    const colsByKey = useMemo(() => {
        const m = new Map();
        for (const c of effectiveColumns)
            m.set(c.key, c);
        return m;
    }, [effectiveColumns]);
    // Columns relevant to the current view mode (grouped hides hideInGrouped)
    const viewColumns = useMemo(() => (groupByPo ? effectiveColumns.filter((c) => !c.hideInGrouped) : effectiveColumns), [effectiveColumns, groupByPo]);
    // Visible = every always-visible column (priority 0/undefined) + the longest
    // prefix of droppable columns (ascending priority) that fits the measured
    // width. Strict priority order — Method drops before Conf before
    // Classification before Description; never the other way around.
    const hasActions = !!renderFooterActions;
    const CELL_CHROME_PX = 25; // horizontal padding + border per cell
    const estColPx = (c) => (c.minPx ?? (c.width ? parseInt(c.width, 10) || 110 : 110)) + CELL_CHROME_PX;
    const visibleColumns = useMemo(() => {
        if (containerWidth == null)
            return viewColumns; // not measured yet
        const core = viewColumns.filter((c) => !(c.priority && c.priority > 0));
        const droppable = viewColumns
            .filter((c) => (c.priority ?? 0) > 0)
            .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        // body padding + expander column + actions column
        const budget = containerWidth - 26 - 30 - (hasActions ? 62 : 0);
        let used = core.reduce((sum, c) => sum + estColPx(c), 0);
        const visible = new Set(core.map((c) => c.key));
        for (const c of droppable) {
            if (used + estColPx(c) > budget)
                break;
            visible.add(c.key);
            used += estColPx(c);
        }
        return viewColumns.filter((c) => visible.has(c.key));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewColumns, containerWidth, hasActions]);
    const hiddenColumns = useMemo(() => viewColumns.filter((c) => !visibleColumns.includes(c)), [viewColumns, visibleColumns]);
    const hasHidden = hiddenColumns.length > 0;
    // In priority mode (fixed table layout), px width hints could sum past the
    // container and push Value off-screen. Convert them to percentages of the
    // width left after the fixed expander/actions columns, so the table always
    // fits exactly and Value is never horizontally scrolled away.
    const priorityColWidths = useMemo(() => {
        if (containerWidth == null || containerWidth <= 0)
            return null;
        const fixedPx = (hasHidden ? 30 : 0) + (hasActions ? 60 : 0);
        const availablePct = Math.max(10, 100 - (fixedPx / containerWidth) * 100);
        const total = visibleColumns.reduce((sum, c) => sum + estColPx(c), 0) || 1;
        const m = new Map();
        for (const c of visibleColumns) {
            m.set(c.key, `${((estColPx(c) / total) * availablePct).toFixed(2)}%`);
        }
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerWidth, visibleColumns, hasHidden, hasActions]);
    const withPriorityWidth = useCallback((col) => {
        const pct = priorityColWidths?.get(col.key);
        return pct ? { ...col, width: pct } : col;
    }, [priorityColWidths]);
    // Below this width the always-visible columns cannot fit even at their
    // minimums — switch to the stacked card presentation.
    const phoneCards = containerWidth != null && containerWidth < 520;
    // Apply time period filter (client-side, before column filters).
    // Inert when a fixed timeScope is set — the window is already enforced
    // by the fetch and double-filtering against "today - N days" would
    // silently drop rows the aggregate counted.
    const timeFilteredRows = useMemo(() => {
        if (timeScope || !showTimePeriodFilter || timePeriod === 'all')
            return rows;
        return rows.filter(r => rowMatchesTimePeriod(r, timePeriod));
    }, [rows, showTimePeriodFilter, timePeriod, timeScope]);
    // Apply column filters
    const filteredRows = useMemo(() => {
        const activeKeys = Object.keys(filters).filter((k) => filters[k] && isFilterActive(filters[k], colsByKey.get(k)?.filterMode ?? null));
        if (activeKeys.length === 0)
            return timeFilteredRows;
        return timeFilteredRows.filter((r) => activeKeys.every((k) => rowMatchesFilter(r, colsByKey.get(k), filters[k])));
    }, [timeFilteredRows, filters, colsByKey]);
    // Apply sort
    const sortedRows = useMemo(() => {
        if (!sort)
            return filteredRows;
        const col = colsByKey.get(sort.col);
        if (!col)
            return filteredRows;
        const dir = sort.dir === 'desc' ? -1 : 1;
        return [...filteredRows].sort((a, b) => dir * compareValues(getCellValue(a, col), getCellValue(b, col), col.sortType || 'string'));
    }, [filteredRows, sort, colsByKey]);
    // Build PO groups for grouped view
    const groupedData = useMemo(() => {
        if (!sortedRows.length)
            return [];
        const groups = new Map();
        for (const row of sortedRows) {
            const poNum = row.purchase_document_num || 'NO_PO';
            if (!groups.has(poNum)) {
                groups.set(poNum, { poNum, lines: [], lineCount: 0, totalAmount: 0 });
            }
            const g = groups.get(poNum);
            g.lines.push(row);
            g.lineCount += 1;
            g.totalAmount += Number(row.line_total) || 0;
        }
        const list = [...groups.values()];
        if (!sort) {
            list.sort((a, b) => b.totalAmount - a.totalAmount);
        }
        return list;
    }, [sortedRows, sort]);
    // Calculate displayed spend
    const displaySpend = useMemo(() => sortedRows.reduce((sum, r) => sum + (Number(r.line_total) || 0), 0), [sortedRows]);
    // Toggle PO expand
    const togglePo = useCallback((poNum) => {
        setExpandedPos((prev) => {
            const next = new Set(prev);
            if (next.has(poNum))
                next.delete(poNum);
            else
                next.add(poNum);
            return next;
        });
    }, []);
    const expandAll = useCallback(() => {
        setExpandedPos(new Set(groupedData.map((g) => g.poNum)));
    }, [groupedData]);
    const collapseAll = useCallback(() => setExpandedPos(new Set()), []);
    // Handle filter change
    const handleFilterChange = useCallback((colKey, filter) => {
        setFilters((prev) => {
            const out = { ...prev };
            if (filter == null)
                delete out[colKey];
            else
                out[colKey] = filter;
            return out;
        });
    }, []);
    // Active filter count
    const activeFilterCount = useMemo(() => Object.entries(filters).filter(([k, f]) => isFilterActive(f, colsByKey.get(k)?.filterMode ?? null)).length, [filters, colsByKey]);
    const clearAllFilters = useCallback(() => {
        setFilters({});
        setSort(null);
    }, []);
    // Reconciliation vs the source aggregate (e.g. clicked bar segment).
    // Compares the FULL result-set total (totalSpend) — not the page sum —
    // in integer cents with a 1-cent tolerance.
    const reconcileState = useMemo(() => {
        if (!reconcile || reconcile.expected == null || isLoading || error)
            return null;
        const actual = Number(totalSpend) || 0;
        const expected = Number(reconcile.expected) || 0;
        return {
            actual,
            expected,
            ok: centsEqual(actual, expected),
            diff: actual - expected,
            label: reconcile.label || 'segment',
        };
    }, [reconcile, totalSpend, isLoading, error]);
    useEffect(() => {
        if (reconcileState && !reconcileState.ok) {
            console.warn('[TransactionTable] Reconciliation mismatch', {
                expected: reconcileState.expected,
                actual: reconcileState.actual,
                diff: reconcileState.diff,
                scope: {
                    vendor,
                    departmentId,
                    component,
                    startDate,
                    endDate,
                    confidenceMode,
                    timePeriod,
                },
            });
        }
    }, [reconcileState, vendor, departmentId, component, startDate, endDate, confidenceMode, timePeriod]);
    // Evidence trust line — plain-language replacement for the confidence
    // toggle. Says how much of the scope's attribution is evidence-backed and
    // whether the lower-confidence remainder is included in the total shown.
    const evidenceNote = useMemo(() => {
        if (!evidenceSplit || isLoading || error)
            return null;
        const lowSpend = (evidenceSplit.all.spend || 0) - (evidenceSplit.evidenceBacked.spend || 0);
        const lowRows = (evidenceSplit.all.rowCount || 0) - (evidenceSplit.evidenceBacked.rowCount || 0);
        if (lowRows <= 0 && Math.abs(lowSpend) < 0.01) {
            return { text: 'All attribution evidence-backed', low: false };
        }
        const amount = `${fmtCurrencyExact(lowSpend)} (${lowRows.toLocaleString()} row${lowRows === 1 ? '' : 's'})`;
        return evidenceSplit.scope === 'confirmed'
            ? { text: `Evidence-backed only — excludes ${amount} of lower-confidence attribution`, low: true }
            : { text: `Includes ${amount} of lower-confidence attribution`, low: true };
    }, [evidenceSplit, isLoading, error]);
    // Pagination
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const hasNextPage = page < totalPages - 1;
    const hasPrevPage = page > 0;
    return (_jsxs("div", { className: `tt-container ${className}`, ref: containerRef, children: [timeScope ? (_jsx("div", { className: "tt-time-filter", children: _jsxs("span", { className: "tt-time-scope", title: "The exact window of the aggregate you clicked \u2014 rows and reconciliation are scoped to it", children: [_jsx("span", { className: "tt-time-scope-key", children: "Window" }), timeScope.label && _jsx("strong", { children: timeScope.label }), _jsx("span", { children: timeScope.startDate || timeScope.endDate
                                ? `${fmtDateFull(timeScope.startDate) ?? 'Start'} – ${fmtDateFull(timeScope.endDate) ?? 'Present'}`
                                : 'All time' })] }) })) : showTimePeriodFilter && (_jsx(TimePeriodFilter, { selected: timePeriod, onChange: handleTimePeriodChange, totalCount: rows.length, filteredCount: timeFilteredRows.length })), _jsxs("div", { className: "tt-header", children: [_jsxs("div", { className: "tt-header-left", children: [_jsx("div", { className: "tt-count", children: isLoading ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { size: 14, className: "tt-spin" }), " Loading\u2026"] })) : error ? (_jsxs("span", { className: "tt-error", children: [_jsx(AlertCircle, { size: 14 }), " ", error.message] })) : (_jsxs(_Fragment, { children: [_jsx("strong", { children: sortedRows.length.toLocaleString() }), " rows", activeFilterCount > 0 && sortedRows.length !== rows.length && (_jsxs("span", { className: "tt-td--muted", children: [' ', "of ", rows.length.toLocaleString()] })), totalRowCount > rows.length && (_jsxs("span", { className: "tt-td--muted", children: [" \u00B7 ", totalRowCount.toLocaleString(), " total"] })), ' · ', _jsx("strong", { children: fmtCurrency(displaySpend) }), totalRowCount > rows.length && Number(totalSpend) > 0 && (_jsxs("span", { className: "tt-td--muted", children: [" shown of ", fmtCurrency(totalSpend)] })), groupByPo && groupedData.length > 0 && (_jsxs("span", { style: { color: 'var(--accent)' }, children: [' ', "\u00B7 ", groupedData.length, " POs"] }))] })) }), reconcileState && (_jsx("span", { className: `tt-reconcile ${reconcileState.ok ? 'tt-reconcile--ok' : 'tt-reconcile--bad'}`, title: reconcileState.ok
                                    ? 'Row total matches the clicked segment (±1¢)'
                                    : 'Row total does NOT match the clicked segment — see console for the breakdown', children: reconcileState.ok ? (_jsxs(_Fragment, { children: ["Rows total ", fmtCurrencyExact(reconcileState.actual), " =", ' ', reconcileState.label, " ", fmtCurrencyExact(reconcileState.expected), " \u2713"] })) : (_jsxs(_Fragment, { children: ["\u2717 Rows ", fmtCurrencyExact(reconcileState.actual), " vs ", reconcileState.label, ' ', fmtCurrencyExact(reconcileState.expected), " \u2014 differs by", ' ', fmtCurrencyExact(Math.abs(reconcileState.diff))] })) })), evidenceNote && (_jsx("span", { className: `tt-evidence ${evidenceNote.low ? 'tt-evidence--low' : 'tt-evidence--ok'}`, title: "Evidence-backed = the supplier or line description literally names the vendor, or classification confidence is \u2265 85%. Lower-confidence rows are the classifier's best guess without that evidence.", children: evidenceNote.text })), activeFilterCount > 0 && (_jsxs("button", { className: "tt-btn tt-btn-ghost", onClick: clearAllFilters, title: "Clear all filters + sort", children: ["Clear ", activeFilterCount, " filter", activeFilterCount !== 1 ? 's' : ''] }))] }), _jsxs("div", { className: "tt-header-right", children: [!hideConfidenceToggle && (_jsxs("div", { className: "tt-confidence-toggle", children: [_jsx("button", { className: `tt-confidence-btn ${confidenceMode === 'confirmed' ? 'is-active' : ''}`, onClick: () => handleConfidenceModeChange('confirmed'), title: "Strict vendor evidence required (default)", children: "Confirmed" }), _jsx("button", { className: `tt-confidence-btn ${confidenceMode === 'probable' ? 'is-active' : ''}`, onClick: () => handleConfidenceModeChange('probable'), title: "Confidence \u2265 75% without strict vendor evidence", children: "Probable" }), _jsx("button", { className: `tt-confidence-btn ${confidenceMode === 'all' ? 'is-active' : ''}`, onClick: () => handleConfidenceModeChange('all'), title: "All classifications including low confidence", children: "All" })] })), groupByPo && groupedData.length > 0 && (_jsxs("div", { style: { display: 'flex', gap: '0.25rem' }, children: [_jsx("button", { className: "tt-btn tt-btn-ghost", onClick: expandAll, children: "Expand all" }), _jsx("button", { className: "tt-btn tt-btn-ghost", onClick: collapseAll, children: "Collapse" })] })), _jsxs("button", { className: `tt-btn ${groupByPo ? 'is-active' : ''}`, onClick: () => {
                                    setGroupByPo(!groupByPo);
                                    setExpandedPos(new Set());
                                }, title: groupByPo ? 'Switch to flat line-items view' : 'Group by PO', children: [groupByPo ? _jsx(Layers, { size: 14 }) : _jsx(List, { size: 14 }), _jsx("span", { children: groupByPo ? 'Grouped' : 'Line items' })] }), _jsx(CopyMenu, { rows: sortedRows, context: copyContext })] })] }), _jsxs("div", { className: "tt-body", children: [!isLoading && !error && sortedRows.length === 0 && (_jsxs("div", { className: "tt-empty", children: [_jsx(AlertTriangle, { size: 28, className: "tt-empty-icon" }), _jsx("p", { children: emptyMessage })] })), sortedRows.length > 0 && !phoneCards && !groupByPo && (_jsxs("table", { className: `tt-table ${dense ? 'tt-table--dense' : ''} ${containerWidth != null ? 'tt-table--priority' : ''}`, children: [_jsx("thead", { children: _jsxs("tr", { children: [hasHidden && _jsx("th", { className: "tt-th tt-expander-col", "aria-label": "Details" }), visibleColumns.map((col) => (_jsx(ColumnHeader, { col: withPriorityWidth(col), sort: sort, filter: filters[col.key] ?? null, rows: rows, onSortChange: setSort, onFilterChange: handleFilterChange }, col.key))), hasActions && (_jsx("th", { className: "tt-th tt-actions-cell", "aria-label": "Actions" }))] }) }), _jsx("tbody", { children: sortedRows.map((r) => {
                                    const isLower = isLowerConfidenceRow(r, confidenceMode);
                                    const isDetailOpen = expandedDetails.has(r.id);
                                    return (_jsxs(React.Fragment, { children: [_jsxs("tr", { className: isLower ? 'tt-row-lower-confidence' : '', onClick: () => onRowClick?.(r), style: { cursor: onRowClick ? 'pointer' : 'default' }, children: [hasHidden && (_jsx("td", { className: "tt-td tt-expander-col", children: _jsx(DetailToggle, { expanded: isDetailOpen, onToggle: () => toggleDetail(r.id) }) })), visibleColumns.map((col) => {
                                                        // PO column with render slot
                                                        if (col.key === 'purchase_document_num' && renderPoLink) {
                                                            return (_jsx("td", { className: "tt-td tt-td--mono", children: renderPoLink(r) }, col.key));
                                                        }
                                                        return (_jsx(RenderCell, { col: col, row: r, confidenceMode: confidenceMode, renderClassification: renderClassification }, col.key));
                                                    }), hasActions && (_jsx("td", { className: "tt-td tt-actions-cell", children: renderFooterActions(r) }))] }), hasHidden && isDetailOpen && (_jsx(HiddenDetailRow, { row: r, hiddenColumns: hiddenColumns, colSpan: visibleColumns.length + 1 + (hasActions ? 1 : 0) }))] }, r.id));
                                }) })] })), sortedRows.length > 0 && !phoneCards && groupByPo && (_jsxs("table", { className: `tt-table ${dense ? 'tt-table--dense' : ''} ${containerWidth != null ? 'tt-table--priority' : ''}`, children: [_jsx("thead", { children: _jsxs("tr", { children: [hasHidden && _jsx("th", { className: "tt-th tt-expander-col", "aria-label": "Details" }), visibleColumns.map((col) => {
                                            const sized = withPriorityWidth(col);
                                            return (_jsx(ColumnHeader, { col: col.key === 'purchase_document_num' ? { ...sized, label: 'PO # / Line' } : sized, sort: sort, filter: filters[col.key] ?? null, rows: rows, onSortChange: setSort, onFilterChange: handleFilterChange }, col.key));
                                        }), hasActions && (_jsx("th", { className: "tt-th tt-actions-cell", "aria-label": "Actions" }))] }) }), _jsx("tbody", { children: groupedData.map((g) => (_jsx(PoGroupRow, { poGroup: g, columns: visibleColumns, hiddenColumns: hiddenColumns, expandedDetails: expandedDetails, onToggleDetail: toggleDetail, isExpanded: expandedPos.has(g.poNum), onToggle: () => togglePo(g.poNum), confidenceMode: confidenceMode, renderClassification: renderClassification, renderPoLink: renderPoLink, renderFooterActions: renderFooterActions, onRowClick: onRowClick }, g.poNum))) })] })), sortedRows.length > 0 && phoneCards && (_jsx("div", { className: "tt-cards", children: groupByPo
                            ? groupedData.map((g) => {
                                const first = g.lines[0];
                                const open = expandedPos.has(g.poNum);
                                return (_jsxs("div", { className: "tt-card", children: [_jsxs("button", { type: "button", className: "tt-card-head tt-card-head--btn", onClick: () => togglePo(g.poNum), "aria-expanded": open, children: [_jsx("span", { className: "tt-card-po", children: renderPoLink && first ? renderPoLink(first) : (g.poNum === 'NO_PO' ? 'No PO' : g.poNum) }), _jsx("span", { className: "tt-card-value", children: fmtCurrency(g.totalAmount) })] }), _jsxs("div", { className: "tt-card-meta", children: [_jsx("span", { children: first?.agency || '—' }), _jsx("span", { children: fmtDateFull(first?.start_date ?? null) || '—' }), _jsxs("span", { children: [g.lineCount, " line", g.lineCount === 1 ? '' : 's'] })] }), first?.supplier_name && _jsx("div", { className: "tt-card-partner", children: first.supplier_name }), open && (_jsx("div", { className: "tt-card-lines", children: g.lines.map((r) => (_jsx(PhoneLineCard, { row: r, confidenceMode: confidenceMode, renderClassification: renderClassification, renderFooterActions: renderFooterActions }, r.id))) }))] }, g.poNum));
                            })
                            : sortedRows.map((r) => (_jsx(PhoneLineCard, { row: r, confidenceMode: confidenceMode, showPo: true, renderClassification: renderClassification, renderFooterActions: renderFooterActions }, r.id))) }))] }), !isDirectMode && totalPages > 1 && (_jsxs("div", { className: "tt-pagination", children: [_jsxs("div", { className: "tt-pagination-info", children: ["Page ", page + 1, " of ", totalPages, " \u00B7 ", totalRowCount.toLocaleString(), " total rows"] }), _jsxs("div", { className: "tt-pagination-controls", children: [_jsxs("button", { className: "tt-btn", disabled: !hasPrevPage, onClick: () => setPage((p) => p - 1), children: [_jsx(ChevronLeft, { size: 14 }), " Prev"] }), _jsxs("button", { className: "tt-btn", disabled: !hasNextPage, onClick: () => setPage((p) => p + 1), children: ["Next ", _jsx(ChevronRight, { size: 14 })] })] })] }))] }));
}
export default TransactionTable;
